import { FC, useMemo, useState } from "react"
import { ArrowRightLeft, AlertTriangle, Info } from "lucide-react"
import { Brand, LoanOfficer } from "../../../../types"
import LoanOfficerAutocomplete from "../../loan-officer-autocomplete"
import { PrimaryBtn } from "../../buttons"
import Dialog from "../../dialog"
import loTransfer from "../../../../api/lo-transfer"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

interface Props {
  brands: Brand[]
}

const LoTransferAssets: FC<Props> = ({ brands }) => {
  const dispatch = useAppDispatch()
  const [brandId, setBrandId] = useState<string>("")
  const [brandName, setBrandName] = useState<string>("")
  const [fromLoid, setFromLoid] = useState<string>("")
  const [fromLoDisabled, setFromLoDisabled] = useState(false)
  const [toLoid, setToLoid] = useState<string>("")
  const [toLoDisabled, setToLoDisabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const canTransfer = brandId && fromLoid && toLoid && fromLoid !== toLoid

  const confirmBody = useMemo(() => {
    const parts = [
      "Are you sure you want to transfer all assets from the source loan officer to the target loan officer?"
    ]
    if (fromLoDisabled || toLoDisabled) {
      const who =
        fromLoDisabled && toLoDisabled
          ? "Both loan officers are"
          : fromLoDisabled
          ? "The source loan officer is"
          : "The target loan officer is"
      parts.push(`${who} currently disabled and will be re-enabled.`)
    }
    parts.push("This action cannot be undone.")
    return parts.join(" ")
  }, [fromLoDisabled, toLoDisabled])

  const handleTransfer = async () => {
    setShowConfirm(false)
    setIsLoading(true)

    try {
      // Re-enable disabled LOs before transferring
      if (fromLoDisabled) {
        await loTransfer.undeleteLo({ loid: parseInt(fromLoid, 10) })
      }
      if (toLoDisabled) {
        await loTransfer.undeleteLo({ loid: parseInt(toLoid, 10) })
      }

      await loTransfer.transferAssets({
        from_loid: parseInt(fromLoid, 10),
        to_loid: parseInt(toLoid, 10)
      })

      dispatch(
        pushToast({
          msg: "Assets transferred successfully",
          theme: "success",
          timeout: 3000
        })
      )

      // Reset form
      setBrandId("")
      setBrandName("")
      setFromLoid("")
      setFromLoDisabled(false)
      setToLoid("")
      setToLoDisabled(false)
    } catch (error: any) {
      dispatch(
        pushToast({
          msg: error?.message || "Failed to transfer assets",
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleFromLoSelect = (lo: LoanOfficer) => {
    setBrandId(lo.bank_nid)
    setFromLoDisabled(lo.active === false)
    // Look up brand name from brands list since API doesn't always return it
    const brand = brands.find(b => b.id === lo.bank_nid)
    setBrandName(brand?.name ?? lo.brand_name ?? "")
    setToLoid("")
    setToLoDisabled(false)
  }

  const handleFromLoChange = (val: string) => {
    setFromLoid(val)
    if (!val) {
      setBrandId("")
      setBrandName("")
      setFromLoDisabled(false)
      setToLoid("")
      setToLoDisabled(false)
    }
  }

  const handleToLoSelect = (lo: LoanOfficer) => {
    setToLoDisabled(lo.active === false)
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <ArrowRightLeft className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              LO Asset Transfer
            </h3>
            <p className="text-sm text-gray-500">
              Transfer all assets from one loan officer to another
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">This action transfers all assets</p>
            <p className="mt-1 text-amber-700">
              Leads, SMS threads, integrations, rateflows, API keys, and pricing
              sets will be moved. Both loan officers must be in the same brand.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Loan Officer:
            </label>
            <LoanOfficerAutocomplete
              searchParams={{ include_disabled: 1 }}
              selected={fromLoid}
              label=""
              direction="vertical"
              showBrandName
              showSearchToggle
              onChange={handleFromLoChange}
              onSelectLo={handleFromLoSelect}
            />
            {fromLoDisabled && (
              <div className="mt-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  This loan officer is currently disabled and will be re-enabled
                  when you transfer.
                </p>
              </div>
            )}
          </div>

          {brandId && (
            <>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Brand:</span> {brandName}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Loan Officer:
                </label>
                <LoanOfficerAutocomplete
                  searchParams={{ brand_id: brandId, include_disabled: 1 }}
                  selected={toLoid}
                  label=""
                  direction="vertical"
                  excludeIds={fromLoid ? [fromLoid] : []}
                  onChange={setToLoid}
                  onSelectLo={handleToLoSelect}
                />
                {toLoDisabled && (
                  <div className="mt-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
                    <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                      This loan officer is currently disabled and will be
                      re-enabled when you transfer.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="pt-2">
          <PrimaryBtn
            onClick={() => setShowConfirm(true)}
            disabled={!canTransfer || isLoading}
            className="w-full justify-center"
          >
            {isLoading ? "Transferring..." : "Transfer Assets"}
          </PrimaryBtn>
        </div>
      </div>

      <Dialog
        open={showConfirm}
        title="Confirm Asset Transfer"
        body={confirmBody}
        okLabel="Yes, Transfer Assets"
        cancelLabel="Cancel"
        onOk={handleTransfer}
        onCancel={() => setShowConfirm(false)}
        onClose={() => setShowConfirm(false)}
        theme="warning"
      />
    </div>
  )
}

export default LoTransferAssets
